import { useContext, useEffect, useRef, useState } from 'react';
import { MolstarRepresentationChangePayload, StructureEventBusContext } from '../contexts/AhojContext';
import { useAhojData } from '../contexts/AhojDataContext';
import { getDefaultChainColor, getDefaultLigandColor, getDefaultPocketColor } from '../contexts/ControlTypes';
import SequenceViewerComponent from './SequenceViewerComponent';
import {
   defaultLigandRepresentation,
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from './molstar/Controls/StructureRepresentationComponent';
import StructureRepresentationControlComponent from './tables/StructureRepresentationControlComponent';

const INITIAL_TOOLBAR_WIDTH = 600;

/**
 * Bottom panel containing:
 * 1. **Global representation/color controls** — dropdowns + color switches that apply
 *    to ALL loaded structures at once (`all: true` dispatches).
 * 2. **Per-type controls** — separate apo and holo control panels that apply only to
 *    sites of that type (dispatched per site via `applyToSites`).
 * 3. **Water toggle** — shows/hides water molecules across all structures.
 * 4. **Sequence viewer** — embeds `SequenceViewerComponent`.
 *
 * Width is user-resizable via a drag handle (mouse drag on the left border), tracked
 * with plain refs so resizing does not trigger React re-renders.
 */
const ControlPanel = (): React.JSX.Element => {
   const {
      setGlobalApoPocketColor,
      setGlobalApoRestColor,
      setGlobalHoloPocketColor,
      setGlobalHoloRestColor,
      setGlobalAlpafoldPocketColor,
      setGlobalAlpafoldRestColor,
   } = useAhojData();

   const [restRepresentation, setRestRepresentation] = useState<Representation>(defaultRestRepresentation);
   const [pocketRepresentation, setPocketRepresentation] = useState<Representation>(defaultPocketRepresentation);
   const [ligandRepresentation, setLigandRepresentation] = useState<Representation>(defaultLigandRepresentation);
   const [restColor, setRestColor] = useState<string>(getDefaultChainColor('apo'));
   const [pocketColor, setPocketColor] = useState<string>(getDefaultPocketColor('apo'));
   const [ligandColor, setLigandColor] = useState<string>(getDefaultLigandColor());

   const [apoRestRepresentation, setApoRestRepresentation] = useState<Representation>(defaultRestRepresentation);
   const [apoPocketRepresentation, setApoPocketRepresentation] = useState<Representation>(defaultPocketRepresentation);
   const [apoRestColor, setApoRestColor] = useState<string>(getDefaultChainColor('apo'));
   const [apoPocketColor, setApoPocketColor] = useState<string>(getDefaultPocketColor('apo'));

   const [holoRestRepresentation, setHoloRestRepresentation] = useState<Representation>(defaultRestRepresentation);
   const [holoPocketRepresentation, setHoloPocketRepresentation] =
      useState<Representation>(defaultPocketRepresentation);
   const [holoLigandRepresentation, setHoloLigandRepresentation] =
      useState<Representation>(defaultLigandRepresentation);
   const [holoRestColor, setHoloRestColor] = useState<string>(getDefaultChainColor('holo'));
   const [holoPocketColor, setHoloPocketColor] = useState<string>(getDefaultPocketColor('holo'));
   const [holoLigandColor, setHoloLigandColor] = useState<string>(getDefaultLigandColor());

   const [alphafoldRestRepresentation, setAlphafoldRestRepresentation] =
      useState<Representation>(defaultRestRepresentation);
   const [alphafoldPocketRepresentation, setAlphafoldPocketRepresentation] =
      useState<Representation>(defaultPocketRepresentation);
   const [alphafoldRestColor, setAlphafoldRestColor] = useState<string>(getDefaultChainColor('alphafold'));
   const [alphafoldPocketColor, setAlphafoldPocketColor] = useState<string>(getDefaultPocketColor('alphafold'));

   const [showWater, setShowWater] = useState<boolean>(false);
   const toolbarRef = useRef<HTMLDivElement>(null);
   const isDragging = useRef(false);
   const dragStartX = useRef(INITIAL_TOOLBAR_WIDTH);
   const dragStartWidth = useRef(INITIAL_TOOLBAR_WIDTH);
   const structureEventBus = useContext(StructureEventBusContext);

   /** Updates global chain (rest) representation and broadcasts to ALL structures (no group filter). */
   const changeRestRepresentation = (type: Representation): void => {
      setRestRepresentation(type);
      structureEventBus.dispatchRepresentationChange({
         type,
         color: restColor,
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'chain',
         representationChanged: true,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /** Updates global pocket representation and broadcasts to ALL structures (no group filter). */
   const changePocketRepresentation = (type: Representation): void => {
      setPocketRepresentation(type);
      structureEventBus.dispatchRepresentationChange({
         type,
         color: pocketColor,
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'pocket',
         representationChanged: true,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /** Updates global ligand representation and broadcasts to ALL structures (no group filter). */
   const changeLigandRepresentation = (type: Representation): void => {
      setLigandRepresentation(type);
      structureEventBus.dispatchRepresentationChange({
         type,
         color: ligandColor,
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'ligand',
         representationChanged: true,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Updates global chain color, broadcasts to ALL structures, and propagates the
    * new color to every per-group global default so that color-pair reset for any
    * group uses this color as its baseline.
    */
   const changeRestColor = (color: string): void => {
      setRestColor(color);
      // Sync all per-group UI state and global defaults.
      setApoRestColor(color);
      setGlobalApoRestColor(color);
      setHoloRestColor(color);
      setGlobalHoloRestColor(color);
      setAlphafoldRestColor(color);
      setGlobalAlpafoldRestColor(color);
      structureEventBus.dispatchRepresentationChange({
         type: restRepresentation,
         color,
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'chain',
         representationChanged: false,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Updates global pocket color, broadcasts to ALL structures, and propagates the
    * new color to every per-group global default so that color-pair reset for any
    * group uses this color as its baseline.
    */
   const changePocketColor = (color: string): void => {
      setPocketColor(color);
      // Sync all per-group UI state and global defaults.
      setApoPocketColor(color);
      setGlobalApoPocketColor(color);
      setHoloPocketColor(color);
      setGlobalHoloPocketColor(color);
      setAlphafoldPocketColor(color);
      setGlobalAlpafoldPocketColor(color);
      structureEventBus.dispatchRepresentationChange({
         type: pocketRepresentation,
         color,
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'pocket',
         representationChanged: false,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /** Updates global ligand color and broadcasts to ALL structures. */
   const changeLigandColor = (color: string): void => {
      setLigandColor(color);
      structureEventBus.dispatchRepresentationChange({
         type: ligandRepresentation,
         color,
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'ligand',
         representationChanged: false,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /** Toggles water visibility across all structures and updates local toggle state. */
   const toggleWater = (): void => {
      const next = !showWater;
      setShowWater(next);
      structureEventBus.dispatch({ type: 'TOGGLE_WATER', payload: { rowId: '', url: '', value: next } });
   };

   /**
    * Handles per-group apo representation changes.
    * Updates the apo global default in AhojDataContext (used by color-pair reset)
    * and dispatches a single grouped event so MolstarComponent updates all apo structures.
    */
   const onApoRepresentationChange = (target: StructureComponentType, type: Representation): void => {
      const color = target === 'chain' ? apoRestColor : apoPocketColor;
      if (target === 'chain') {
         setApoRestRepresentation(type);
      } else {
         setApoPocketRepresentation(type);
      }
      structureEventBus.dispatchRepresentationChange({
         type,
         color,
         allStructuresOfSingleType: true,
         url: '',
         structureComponentType: target,
         representationChanged: true,
         colorChanged: true,
         structureType: 'apo',
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Handles per-group apo color changes.
    * Updates the apo global default in AhojDataContext (the color-pair reset baseline)
    * and dispatches a grouped event to update all apo structures in Molstar.
    */
   const onApoColorChange = (target: StructureComponentType, color: string): void => {
      if (target === 'chain') {
         setApoRestColor(color);
         setGlobalApoRestColor(color);
      } else {
         setApoPocketColor(color);
         setGlobalApoPocketColor(color);
      }
      structureEventBus.dispatchRepresentationChange({
         type: target === 'chain' ? apoRestRepresentation : apoPocketRepresentation,
         color,
         allStructuresOfSingleType: true,
         url: '',
         structureComponentType: target,
         representationChanged: false,
         colorChanged: true,
         structureType: 'apo',
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Handles per-group holo representation changes (covers holo, unobserved, query_pocket).
    * Updates the holo global default in AhojDataContext and dispatches a grouped event.
    */
   const onHoloRepresentationChange = (target: StructureComponentType, type: Representation): void => {
      const color = target === 'chain' ? holoRestColor : target === 'ligand' ? holoLigandColor : holoPocketColor;
      if (target === 'chain') {
         setHoloRestRepresentation(type);
      } else if (target === 'ligand') {
         setHoloLigandRepresentation(type);
      } else {
         setHoloPocketRepresentation(type);
      }
      structureEventBus.dispatchRepresentationChange({
         type,
         color,
         allStructuresOfSingleType: true,
         url: '',
         structureComponentType: target,
         representationChanged: true,
         colorChanged: true,
         structureType: 'holo',
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Handles per-group holo color changes (covers holo, unobserved, query_pocket).
    * Updates the holo global default in AhojDataContext and dispatches a grouped event.
    */
   const onHoloColorChange = (target: StructureComponentType, color: string): void => {
      if (target === 'chain') {
         setHoloRestColor(color);
         setGlobalHoloRestColor(color);
      } else if (target === 'ligand') {
         setHoloLigandColor(color);
      } else {
         setHoloPocketColor(color);
         setGlobalHoloPocketColor(color);
      }
      structureEventBus.dispatchRepresentationChange({
         type:
            target === 'chain'
               ? holoRestRepresentation
               : target === 'ligand'
                 ? holoLigandRepresentation
                 : holoPocketRepresentation,
         color,
         allStructuresOfSingleType: true,
         url: '',
         structureComponentType: target,
         representationChanged: false,
         colorChanged: true,
         structureType: 'holo',
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Handles per-group AlphaFold representation changes.
    * Updates the AlphaFold global default in AhojDataContext and dispatches a grouped event.
    */
   const onAlphafoldRepresentationChange = (target: StructureComponentType, type: Representation): void => {
      const color = target === 'chain' ? alphafoldRestColor : alphafoldPocketColor;
      if (target === 'chain') {
         setAlphafoldRestRepresentation(type);
      } else {
         setAlphafoldPocketRepresentation(type);
      }
      structureEventBus.dispatchRepresentationChange({
         type,
         color,
         allStructuresOfSingleType: true,
         url: '',
         structureComponentType: target,
         representationChanged: true,
         colorChanged: true,
         structureType: 'alphafold',
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Handles per-group AlphaFold color changes.
    * Updates the AlphaFold global default in AhojDataContext and dispatches a grouped event.
    */
   const onAlphafoldColorChange = (target: StructureComponentType, color: string): void => {
      if (target === 'chain') {
         setAlphafoldRestColor(color);
         setGlobalAlpafoldRestColor(color);
      } else {
         setAlphafoldPocketColor(color);
         setGlobalAlpafoldPocketColor(color);
      }
      structureEventBus.dispatchRepresentationChange({
         type: target === 'chain' ? alphafoldRestRepresentation : alphafoldPocketRepresentation,
         color,
         allStructuresOfSingleType: true,
         url: '',
         structureComponentType: target,
         representationChanged: false,
         colorChanged: true,
         structureType: 'alphafold',
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Begins a toolbar-width drag interaction. Records the initial mouse X position
    * and the current toolbar width in refs so the `mousemove` handler can compute
    * the delta without React state overhead.
    */
   const onToolbarResizeStart = (e: React.MouseEvent): void => {
      isDragging.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = toolbarRef.current?.offsetWidth ?? INITIAL_TOOLBAR_WIDTH;
      e.preventDefault();
   };

   useEffect(() => {
      const handleMouseMove = (e: MouseEvent): void => {
         if (!isDragging.current) return;
         if (e.buttons === 0) {
            isDragging.current = false;
            return;
         }

         const delta = e.clientX - dragStartX.current;
         const newWidth = Math.max(220, dragStartWidth.current + delta);
         if (toolbarRef.current) {
            toolbarRef.current.style.flex = `0 0 ${newWidth}px`;
            toolbarRef.current.style.width = `${newWidth}px`;
         }
      };

      const handleMouseUp = (): void => {
         isDragging.current = false;
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
         document.removeEventListener('mousemove', handleMouseMove);
         document.removeEventListener('mouseup', handleMouseUp);
      };
   }, []);

   return (
      <div id="bottom-panel">
         <div
            ref={toolbarRef}
            className="bottom-panel-toolbar"
            style={{ flex: `0 0 ${INITIAL_TOOLBAR_WIDTH}px`, width: INITIAL_TOOLBAR_WIDTH }}
         >
            <div className="bottom-panel-toolbar-header">
               <label className="water-toggle-label">
                  <input type="checkbox" checked={showWater} onChange={toggleWater} className="water-toggle-checkbox" />
                  <span>Water</span>
               </label>
            </div>
            <table className="bottom-panel-control-table" style={{ width: '100%', tableLayout: 'fixed' }}>
               <thead>
                  <tr>
                     <th className="bottom-panel-control-heading" style={{ textAlign: 'center', verticalAlign: 'top' }}>
                        Global (All Structures)
                     </th>
                     <th className="bottom-panel-control-heading" style={{ textAlign: 'center', verticalAlign: 'top' }}>
                        Apo Sites
                     </th>
                     <th className="bottom-panel-control-heading" style={{ textAlign: 'center', verticalAlign: 'top' }}>
                        Holo / Unobserved / Query
                     </th>
                     <th className="bottom-panel-control-heading" style={{ textAlign: 'center', verticalAlign: 'top' }}>
                        AlphaFold Models
                     </th>
                  </tr>
               </thead>
               <tbody>
                  <tr>
                     <td style={{ verticalAlign: 'top', textAlign: 'center' }}>
                        <StructureRepresentationControlComponent
                           mode="small"
                           values={{
                              chain: { representation: restRepresentation, color: restColor },
                              pocket: { representation: pocketRepresentation, color: pocketColor },
                              ligand: { representation: ligandRepresentation, color: ligandColor },
                           }}
                           onRepresentationChange={(target, type): void => {
                              if (target === 'chain') changeRestRepresentation(type);
                              else if (target === 'pocket') changePocketRepresentation(type);
                              else if (target === 'ligand') changeLigandRepresentation(type);
                           }}
                           onColorChange={(target, color): void => {
                              if (target === 'chain') changeRestColor(color);
                              else if (target === 'pocket') changePocketColor(color);
                              else if (target === 'ligand') changeLigandColor(color);
                           }}
                        />
                     </td>
                     <td style={{ verticalAlign: 'top', textAlign: 'center' }}>
                        <StructureRepresentationControlComponent
                           mode="small"
                           values={{
                              chain: { representation: apoRestRepresentation, color: apoRestColor },
                              pocket: { representation: apoPocketRepresentation, color: apoPocketColor },
                           }}
                           onRepresentationChange={onApoRepresentationChange}
                           onColorChange={onApoColorChange}
                        />
                     </td>
                     <td style={{ verticalAlign: 'top', textAlign: 'center' }}>
                        <StructureRepresentationControlComponent
                           mode="small"
                           values={{
                              chain: { representation: holoRestRepresentation, color: holoRestColor },
                              pocket: { representation: holoPocketRepresentation, color: holoPocketColor },
                              ligand: { representation: holoLigandRepresentation, color: holoLigandColor },
                           }}
                           onRepresentationChange={onHoloRepresentationChange}
                           onColorChange={onHoloColorChange}
                        />
                     </td>
                     <td style={{ verticalAlign: 'top', textAlign: 'center' }}>
                        <StructureRepresentationControlComponent
                           mode="small"
                           values={{
                              chain: { representation: alphafoldRestRepresentation, color: alphafoldRestColor },
                              pocket: { representation: alphafoldPocketRepresentation, color: alphafoldPocketColor },
                           }}
                           onRepresentationChange={onAlphafoldRepresentationChange}
                           onColorChange={onAlphafoldColorChange}
                        />
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
         <div className="bottom-panel-resize-handle" onMouseDown={onToolbarResizeStart} />
         <div className="bottom-panel-sequence">
            <SequenceViewerComponent />
         </div>
      </div>
   );
};

export default ControlPanel;
